import { Association, type Opportunity } from "@entities";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { CircularProgress, Stack } from "@mui/material";
import { associationRepo } from "@repositories";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { type QueryObserverResult, type RefetchOptions } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { UserCardPopup } from "../../../components/popups/user-card/UserCardPopup";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useAlert } from "../../../contexts/AlertProvider";
import { useAddUserToOpportunity } from "../../../queries/events.query";
import usePromptMatchWeight, {
  MatchWeightPromptCancelled,
  resolveMatchWeightForAdd,
} from "../../../dialog-components/MatchWeightDialog";
import {
  ADMIN_BULK_ACTION_LIMIT,
  runInChunks,
} from "../../../helpers/admin-list-query";
import columns from "./columns";
import filters from "./filters";
import { OrganizationRoles } from "@shared/Roles";

const OptionalParticipantsTable: FC<{
  id: string;
  refetchEvent: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<Opportunity | null | undefined, Error>>;
  tableId?: string;
}> = ({ id, refetchEvent, tableId = "optional-participants-table" }) => {
  const [currentUserRowData, setCurrentUserRowData] = useState<Association>();
  const { HyperTable } = useCreateHyperTable(Association);
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const { mutateAsync: addUserToOpportunity } = useAddUserToOpportunity();
  const promptMatchWeight = usePromptMatchWeight();

  const buttons: ActionButton<Association>[] = useMemo(() => {
    return [
      {
        icon: <PersonAddAlt1Icon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;

          // Get all associations matching the filter criteria
          const associations = await associationRepo.find({
            where: whereToUpdate,
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });
          const toProcess = associations.slice(0, ADMIN_BULK_ACTION_LIMIT);
          if (toProcess.length === 0) return;

          try {
            const matchWeight = await resolveMatchWeightForAdd({
              opportunityId: id,
              prompt: promptMatchWeight,
            });
            await runInChunks(toProcess, 4, async (row) => {
              await addUserToOpportunity({
                opportunityId: id,
                associationId: row.id,
                showSuccessAlert: false,
                matchWeight: matchWeight ?? 1,
              });
            });
            const truncated = associations.length > ADMIN_BULK_ACTION_LIMIT;
            setAlertText(
              truncated
                ? `נוספו עד ${ADMIN_BULK_ACTION_LIMIT} מתנדבים. חזרו על הפעולה לשאר.`
                : toProcess.length === 1
                  ? "המשתתפים נוספו בהצלחה"
                  : `${toProcess.length} מתנדבים נוספו למשימה`,
            );
            setSeverity("success");
          } catch (error) {
            if (error instanceof MatchWeightPromptCancelled) return;
            throw error;
          }
          refetchEvent();
          HyperTable.context.current?.refetch("remove");
        },
        title: "הוספה למשימה",
      },
    ];
  }, [id, addUserToOpportunity, HyperTable, refetchEvent, promptMatchWeight, setAlertText, setSeverity]);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId={tableId}
      findConfig={{
        where: Association.hyperFilter({
          organizationId: currOrganization.id,
          opportunities: {
            $none: {
              opportunity: { $id: id },
              status: { $in: ["APPROVED", "PENDING"] },
            },
          },
          roles: {
            $some: OrganizationRoles.Volunteer,
          },
          user: {
            $is: {
              isDeleted: false,
            },
          },
        }),
        include: {
          user: true,
        },
      }}
      onRowClick={(_, row) => setCurrentUserRowData(row.original)}
      title={null}
    >
      <HyperTable.Toolbar>
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...filter} />
          ),
        )}
      </HyperTable.Toolbar>
      {columns.map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      <HyperTable.SelectColumn />
      <UserCardPopup
        userData={currentUserRowData}
        setUserData={() => setCurrentUserRowData(undefined)}
        isAdd={false}
        setIsAdd={() => { }}
      />
      <HyperTable.ActionsBar buttons={buttons} />
    </HyperTable>
  );
};

export default OptionalParticipantsTable;
